import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import PoweredBy from "../../Assets/Images/Sidebar/PoweredBy.svg";
import MyNavLink from "./Links/MyNavLink";
import Cookies from "universal-cookie";
import removeAccessToken from "../../Helper/RemoveAccessToken/RemoveAccessToken";
import { Backdrop, Box } from "@mui/material";
import DropdownNavLink from "./Links/DropdownNavLink";
import CustomIcon from "../../Components/CustomIcon";
import { SetSessionExpired } from "../../Actions";
import MenuItem from "../../PagesContent/AdvancedAnalytics/Common/MenuItem";
import AppCache from "../../Helper/Common/AppCache";
import { usePermissions } from "../../Context/PermissionContext";
import { useDashboard } from "../../Context/DashboardContext";
import { routeFeatureCodes, FEATURE_CODES } from "../../Static/FeatureMapping";
import DashboardNavItem from "./DashboardNavItem";
import { isPoweredByVisible } from "../../Helper/Organization/Branding";

const drawerWidth = 245;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "55px",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function MiniDrawer(props) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const { activeDashboard } = useDashboard();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const showPoweredBy = isPoweredByVisible();
  const showFooterLogo = sidebarOpen && showPoweredBy;
  const [expandedAccordion, setExpandedAccordion] = React.useState(null);
  const [hoverOnSidebarIcon, setHoverOnSidebarIcon] = React.useState(false);

  const handleAccordionChange = (value) => {
    setExpandedAccordion(value);
  };

  const handleDrawerClick = () => {
    setSidebarOpen(!sidebarOpen);
    setHoverOnSidebarIcon(false);
  };

  const cookies = new Cookies();
  const navigationCardData = AppCache.getItem("navigationCard");
  const customNavigationCard = AppCache.getItem("customNavigationCard") || [];
  let availablePages = AppCache.getItem("pages");
  availablePages.push("Inventory");
  availablePages = availablePages.filter((value) => value !== "DigitalTwin");

  if (navigationCardData === undefined || availablePages === undefined) {
    removeAccessToken();
  }

  // Build the computed navigation list, grouping BMS Faults V2 + Platform Alerts V2
  // under a single "Alerts" parent when the user has access to both.
  const buildComputedNavigation = () => {
    if (!navigationCardData || activeDashboard !== 'coulomb') return navigationCardData || [];

    const hasPlatformAlerts = hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2, 'view');
    if (!hasPlatformAlerts) return navigationCardData;

    const bmsPage = navigationCardData.find((p) => p.mykey === 'BMSAlertV2');

    const platformAlertsItem = {
      mykey: 'PlatformAlertsV2',
      text: 'Smart Alerts',
      to: '/platform-alerts-v2',
      iconName: 'smart-alert-icon',
      isActive: true,
    };

    if (bmsPage) {
      // Both: replace the BMS Faults standalone item with an "Alerts" dropdown
      return navigationCardData.map((page) => {
        if (page.mykey !== 'BMSAlertV2') return page;
        return {
          title: 'Alerts',
          iconName: 'navbarAlerts',
          listItems: [
            { mykey: page.mykey, text: page.text, to: page.to, iconName: page.iconName, isActive: true },
            { ...platformAlertsItem, isActive: true },
          ],
        };
      });
    }

    // Only Platform Alerts V2 (BMS Faults not in nav card): append as standalone
    return [...navigationCardData, platformAlertsItem];
  };

  const computedNavigationData = buildComputedNavigation();

  // Check if page should be visible based on RBAC v2 permissions
  const isPageVisible = (page) => {
    const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
    const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

    // V1: Use old availablePages check
    if (rbacVersion !== 'v2' || !rbacEnabled) {
      return availablePages.includes(page.mykey);
    }

    // V2: Check RBAC permissions
    // First check if page is in availablePages (backend still controls basic access)
    if (!availablePages.includes(page.mykey)) {
      return false;
    }

    // Then check RBAC permissions for the route
    const featureCodes = routeFeatureCodes(page.to);
    if (featureCodes.length === 0) {
      // No RBAC feature code defined, allow access (backward compatibility)
      return true;
    }

    // User needs at least 'view' on ONE of the route's features. Asset
    // Management is three tabs behind three features and any one of them is
    // enough to reach the page; the tabs themselves are filtered inside.
    return featureCodes.some((code) => hasPermission(code, 'view'));
  };

  return (
    <>
      <Drawer
        variant="permanent"
        open={sidebarOpen}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            border: "0px, 1px, 0px, 0px",
            borderColor: "#E9EFF5",
            paddingLeft: "8px",
            paddingRight: "8px",
          },
        }}
        ModalProps={{ onBackdropClick: () => setSidebarOpen(false) }}
      >
        <List
          style={{
            paddingTop: "64px",
          }}
        >
          {activeDashboard !== 'coulomb'
            ? customNavigationCard.map((page) => {
                // Use exact matching when this path is a prefix of another custom nav path
                // (e.g. /custom-observability must not stay active when on /custom-observability/inventory)
                const needsEnd = customNavigationCard.some(
                  (other) => other.to !== page.to && other.to.startsWith(page.to)
                );
                return (
                  <MyNavLink
                    key={page.mykey}
                    to={page.to}
                    mykey={page.mykey}
                    textPrimary={page.text}
                    iconName={page.iconName}
                    open={sidebarOpen}
                    end={needsEnd}
                  />
                );
              })
            : computedNavigationData?.map((page, index) => {
                // Mark Session of the user expired if their application still has old navbar
                if (page?.tabs?.length > 0) {
                  dispatch(SetSessionExpired(true));
                  return;
                }

                if (page?.listItems) {
                  const availablePagesFromList = page.listItems.filter((subPage) =>
                    isPageVisible(subPage)
                  );
                  if (availablePagesFromList.length === 0) {
                    return null;
                  }
                  return (
                    <DropdownNavLink
                      name={page.title}
                      key={index}
                      navigationData={{...page, listItems: availablePagesFromList}}
                      sidebarOpen={sidebarOpen}
                      onAccordionChange={handleAccordionChange}
                      isExpanded={expandedAccordion === page.title && sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      availablePages={availablePages}
                    />
                  );
                }

                if (isPageVisible(page)) {
                  return (
                    <MyNavLink
                      to={page.to}
                      mykey={page.mykey}
                      textPrimary={page.text}
                      iconName={page.iconName}
                      key={index}
                      open={sidebarOpen}
                    />
                  );
                }
              })
          }
          <DashboardNavItem
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            isExpanded={expandedAccordion === 'Dashboard' && sidebarOpen}
            onAccordionChange={handleAccordionChange}
          />
        </List>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {activeDashboard === 'coulomb' && availablePages.includes("AdvancedAnalysis") && (
            <MenuItem
              mykey="Advanced Analysis"
              to="/advanced-analysis"
              textPrimary="Advanced Analysis"
              open={sidebarOpen}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: sidebarOpen ? "space-between" : "flex-start",
              mb: 1.5,
              pl: sidebarOpen ? 2.5 : 0,
              pr: sidebarOpen ? 1 : 0,
              ...(showFooterLogo && {
                mt: 2,
                pt: 1.5,
                borderTop: "1px solid #E6E6E6",
              }),
            }}
          >
            {showFooterLogo && (
              <img
                src={PoweredBy}
                alt="Powered by Coulomb AI"
                data-testid="sidebar-powered-by"
                style={{ height: "28px", width: "auto", opacity: 0.85, display: "block" }}
              />
            )}
            <IconButton
              disableRipple
              data-testid="sidebar-toggle"
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              onClick={handleDrawerClick}
              onMouseEnter={() => {
                setHoverOnSidebarIcon(true);
              }}
              onMouseLeave={() => {
                setHoverOnSidebarIcon(false);
              }}
              sx={{ ml: sidebarOpen ? "auto" : "0" }}
            >
              <CustomIcon
                name={sidebarOpen ? "closeSidebar" : "expandSidebar"}
                fill={hoverOnSidebarIcon ? "#6D72F6" : "#9B9B9B"}
                style={{ fontSize: 20 }}
              />
            </IconButton>
          </Box>
        </Box>
      </Drawer>
      <Backdrop
        sx={{
          zIndex: theme.zIndex.drawer - 1,
          color: "#fff",
        }}
        open={sidebarOpen}
        onClick={() => setSidebarOpen(false)}
      ></Backdrop>
    </>
  );
}

export default MiniDrawer;
